// client/src/components/tax/TaxCenter.jsx
//
// /:tenant/tax-center — admin Tax Center (owners / admins). Year selector,
// generation status, missing-data warnings, draft preview, issue final,
// corrections, filer packet, and the payment-mode-aware Form 1099-K card.
// Tabs: Overview · Documents · Raffle winners · FMV & prizes · Auctions · Settings.
//
// API: /api/tax (server/routes/taxRoutes.js); tenant from the session.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaDownload, FaFileAlt, FaHome, FaSync } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import TaxDocuments from './TaxDocuments';
import TaxWinners from './TaxWinners';
import TaxFmv from './TaxFmv';
import TaxAuctions from './TaxAuctions';
import TaxSettings from './TaxSettings';
import { when } from './format';
import { getOverview, startRun, adminZipUrl, documentUrl, listDocuments, errorText } from '../../services/taxApi';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import '../reports/Reports.css';
import './Tax.css';

const TABS = ['overview', 'documents', 'winners', 'fmv', 'auctions', 'settings'];


function Warnings({ warnings, t }) {
  if (!warnings || !warnings.length) {
    return <p className="surface-muted perf-small">{t('tax.warnings.none', 'No missing information for this year.')}</p>;
  }
  const label = { blocker: t('tax.sev.blocker', 'Blocker'), warning: t('tax.sev.warning', 'Warning'), info: t('tax.sev.info', 'Info') };
  return (
    <ul className="tx-warnings">
      {warnings.map((w) => (
        <li key={w.code} className={`tx-warning ${w.severity}`}>
          <span className={`tx-sev ${w.severity}`}>{label[w.severity] || w.severity}</span>
          {w.message}
          {w.items && w.items.length > 0 && (
            <details>
              <summary>{t('tax.warnings.show', 'Show {{count}}', { count: w.count || w.items.length })}</summary>
              <ul>
                {w.items.map((it, i) => (
                  <li key={i}>{[it.name, it.event, it.raffle, it.lot, it.date].filter(Boolean).join(' · ') || JSON.stringify(it)}</li>
                ))}
              </ul>
            </details>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function TaxCenter({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const tenant = Number(tenantProp || params.tenant);
  const year = Number(search.get('year')) || null;
  const tab = TABS.includes(search.get('tab')) ? search.get('tab') : 'overview';

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [packet, setPacket] = useState(null);
  const poll = useRef(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const o = await getOverview(year);
      setData(o);
      const p = await listDocuments(o.year, { scope: 'admin', type: 'w2g_filer_packet', drafts: true }).catch(() => ({ documents: [] }));
      const docs = p.documents || [];
      setPacket(docs.find((d) => d.status !== 'draft') || docs[0] || null);
    } catch (err) {
      setError(errorText(err, t('tax.loadError', 'Could not load the Tax Center.')));
    }
  }, [year, t]);

  useEffect(() => { load(); }, [load]);

  // Refresh while a run is queued or running.
  useEffect(() => {
    clearTimeout(poll.current);
    if (data && data.runs.some((r) => r.status === 'queued' || r.status === 'running')) {
      poll.current = setTimeout(load, 5000);
    }
    return () => clearTimeout(poll.current);
  }, [data, load]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(search);
    if (v === null || v === undefined || v === '') next.delete(k); else next.set(k, String(v));
    setSearch(next, { replace: true });
  };

  const run = async (mode) => {
    if (mode === 'final' && !window.confirm(t('tax.confirmFinal', 'Issue final {{year}} documents? Issued documents are never changed afterwards — mistakes are fixed by issuing a correction.', { year: data.year }))) return;
    setBusy(mode);
    setNotice('');
    try {
      await startRun(data.year, mode);
      setNotice(mode === 'final' ? t('tax.finalQueued', 'Final documents are being generated. Recipients are notified when they are ready.') : t('tax.draftQueued', 'Draft is being generated. This page refreshes when it is ready.'));
      await load();
    } catch (err) {
      setNotice(errorText(err, t('tax.runError', 'Could not start the run.')));
    } finally {
      setBusy('');
    }
  };

  const migrationPending = error && /tax migrations/i.test(error);
  const statusLabel = {
    queued: t('tax.run.queued', 'Queued'), running: t('tax.run.running', 'Generating'),
    done: t('tax.run.done', 'Done'), failed: t('tax.run.failed', 'Failed'),
  };
  const modeLabel = { draft: t('tax.mode.draft', 'Draft'), final: t('tax.mode.final', 'Final'), correction: t('tax.mode.correction', 'Correction') };
  const counts = data ? data.counts.reduce((m, c) => { m[c.status] = (m[c.status] || 0) + c.count; return m; }, {}) : {};
  const blockers = data ? data.warnings.filter((w) => w.severity === 'blocker').length : 0;

  return (
    <div className="rp-page">
      <section className="surface">
        <div className="tx-head">
          <h2>{t('tax.title', 'Tax Center')}</h2>
          {data && (
            <label className="tx-year">
              {t('tax.year', 'Tax year')}
              <select value={data.year} onChange={(e) => setParam('year', e.target.value)} aria-label={t('tax.year', 'Tax year')}>
                {[...new Set([data.year, ...data.years])].sort((a, b) => b - a).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          )}
        </div>
        <p className="surface-muted perf-small">
          {t('tax.intro', 'Year-end documents for your donors, bidders, attendees and raffle winners, and summaries for your own filing. On-Screen prepares documents; your organization files them.')}
        </p>
        {data && <div className="note warn tx-disclaimer" role="note">{data.disclaimer}</div>}
      </section>

      {error && !migrationPending && <ErrorState message={error} onRetry={load} />}
      {migrationPending && <div className="note warn" role="status">{error}</div>}
      {!error && !data && <LoadingState />}

      {data && (
        <>
          <div className="fo-tabs" role="tablist" aria-label={t('tax.title', 'Tax Center')}>
            {TABS.map((k) => (
              <button key={k} type="button" role="tab" className="fo-tab" aria-selected={tab === k} onClick={() => setParam('tab', k === 'overview' ? '' : k)}>
                {t(`tax.tab.${k}`, { overview: 'Overview', documents: 'Documents', winners: 'Raffle winners', fmv: 'FMV & prizes', auctions: 'Auctions', settings: 'Settings' }[k])}
              </button>
            ))}
          </div>

          {notice && <div className="note info" role="status">{notice}</div>}

          {tab === 'overview' && (
            <>
              <section className="surface" aria-labelledby="tx-gen-title">
                <h4 id="tx-gen-title">{t('tax.generation', 'Generation for {{year}}', { year: data.year })}</h4>
                <div className="tx-grid">
                  <div className="tx-card">
                    <h5>{t('tax.statusHeading', 'Status')}</h5>
                    <dl className="tx-kv">
                      <dt>{t('tax.issued', 'Issued')}</dt><dd>{(counts.final || 0) + (counts.corrected || 0)}</dd>
                      <dt>{t('tax.drafts', 'Drafts')}</dt><dd>{counts.draft || 0}</dd>
                      <dt>{t('tax.corrections', 'Corrections')}</dt><dd>{counts.corrected || 0}</dd>
                    </dl>
                    {data.canIssue && !data.canFinalize && <p>{t('tax.finalAfterYear', 'Final documents can be issued once {{year}} has ended.', { year: data.year })}</p>}
                    {!data.canIssue && <p>{t('tax.issuedByOnScreen', 'On-Screen issues final documents and sends them to your donors and winners. Generate a draft any time to review what they will receive.')}</p>}
                  </div>
                  <div className="tx-card accent">
                    <h5>{t('tax.form1099k.title', 'Form 1099-K')}</h5>
                    <p>{data.form1099k.text}</p>
                    {data.form1099k.stripeUrl && (
                      <a href={data.form1099k.stripeUrl} target="_blank" rel="noopener noreferrer">{t('tax.form1099k.link', 'Open Stripe tax forms')}</a>
                    )}
                  </div>
                </div>
                <div className="tx-actions" style={{ marginTop: 10 }}>
                  <button type="button" className="lm-btn secondary" disabled={!!busy} onClick={() => run('draft')}>
                    <FaSync aria-hidden="true" /> {busy === 'draft' ? t('tax.starting', 'Starting…') : t('tax.runDraft', 'Generate draft')}
                  </button>
                  {data.canIssue && data.canFinalize && (
                    <button type="button" className="lm-btn" disabled={!!busy || blockers > 0} onClick={() => run('final')} title={blockers ? t('tax.fixBlockers', 'Fix the blockers first') : undefined}>
                      <FaFileAlt aria-hidden="true" /> {t('tax.runFinal', 'Issue final documents')}
                    </button>
                  )}
                  {counts.draft > 0 && (
                    <a className="lm-btn secondary" href={adminZipUrl(data.year, { scope: 'all', drafts: true })}><FaDownload aria-hidden="true" /> {t('tax.downloadDrafts', 'Download drafts')}</a>
                  )}
                  {(counts.final || counts.corrected) > 0 && (
                    <a className="lm-btn" href={adminZipUrl(data.year)}><FaDownload aria-hidden="true" /> {t('tax.downloadMine', 'Download my tax documents')}</a>
                  )}
                  {packet && (
                    <a className="lm-btn secondary" href={documentUrl(packet.id)}><FaDownload aria-hidden="true" /> {t('tax.filerPacket', 'W-2G filer packet')}{packet.status === 'draft' ? ` (${t('tax.mode.draft', 'Draft')})` : ''}</a>
                  )}
                </div>
                {data.runs.length > 0 && (
                  <>
                    <h5 style={{ margin: '12px 0 4px' }}>{t('tax.recentRuns', 'Recent runs')}</h5>
                    <ul className="tx-runs">
                      {data.runs.map((r) => (
                        <li key={r.id}>
                          <span>{modeLabel[r.mode] || r.mode} · {when(r.queued_at)}</span>
                          <span>
                            <span className={`tx-status ${r.status === 'failed' ? 'failed' : ''}`}>{statusLabel[r.status] || r.status}</span>
                            {r.summary && ` · ${t('tax.runSummary', '{{created}} created, {{skipped}} unchanged', { created: r.summary.created || 0, skipped: r.summary.skipped || 0 })}`}
                            {r.summary && r.summary.blocked && r.summary.blocked.length > 0 && ` · ${t('tax.runBlocked', '{{count}} waiting on missing information', { count: r.summary.blocked.length })}`}
                            {r.summary && r.summary.reconciliation && r.summary.reconciliation.status === 'mismatch' && ` · ${t('tax.reconMismatch', 'Stripe mismatch — see revenue summary')}`}
                            {r.status === 'failed' && r.error && ` — ${r.error}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>

              <section className="surface" aria-labelledby="tx-warn-title">
                <h4 id="tx-warn-title">{t('tax.warnings.title', 'Missing information')}</h4>
                <Warnings warnings={data.warnings} t={t} />
              </section>
            </>
          )}

          {tab === 'documents' && <TaxDocuments year={data.year} canIssue={!!data.canIssue} onChanged={load} />}
          {tab === 'winners' && <TaxWinners year={data.year} />}
          {tab === 'fmv' && <TaxFmv year={data.year} nonprofit={data.profile.exemptStatus !== 'none'} />}
          {tab === 'auctions' && <TaxAuctions year={data.year} />}
          {tab === 'settings' && <TaxSettings profile={data.profile} onSaved={load} />}
        </>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
          { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}
